import { useCallback, useMemo, useRef, useState } from 'react';
import { FlatList, LayoutChangeEvent } from 'react-native';
import Animated, { useAnimatedStyle, useSharedValue } from 'react-native-reanimated';
import {
  GRID_HEADER_HEIGHT,
  GRID_ITEM_ACTIONS_HEIGHT,
  GRID_ITEM_TITLE_HEIGHT,
} from 'src/constants/expandableGroup.constants';
import { useStore } from 'src/contexts/StoreContext';
import { useDebounceCallback } from 'src/hooks/useDebounceCallback';
import { IUserImageAreaModel } from 'src/store/_models/userImageArea';
import CropPreview from '../../../product-preview/crop-preview/CropPreview';
import useResponsiveScreen from 'src/hooks/useResponsiveScreen';

export const ImagesGrid = ({
  userImageAreas,
  numColumns,
}: {
  userImageAreas: IUserImageAreaModel[];
  numColumns: number;
}) => {
  const {
    project: { activeProject },
  } = useStore();

  const { isDesktop } = useResponsiveScreen();
  const flatListRef = useRef<FlatList>(null);
  const isGrid = useMemo(() => numColumns > 0, [numColumns]);
  const isSlideshow = useMemo(() => numColumns === 0, [numColumns]);
  const [scrollEnabled, setScrollEnabled] = useState(true);
  const containerDimensionsSV = useSharedValue<{ width: number; height: number }>({
    width: 0,
    height: 0,
  });
  const itemSizeSV = useSharedValue<{ width: number; height: number; paddingHorizontal: number }>({
    width: 0,
    height: 0,
    paddingHorizontal: 0,
  });

  const onCurrentProductChangedDebounced = useDebounceCallback((productId: string) => {
    // console.log("Central element changed (activeProject?.updateProductPriority): ", {productId});
    activeProject?.updateProductPriority(productId);
  }, 400);

  const handlePanStart = useCallback(() => {
    setScrollEnabled(false);
  }, []);

  const handlePanEnd = useCallback(() => {
    setScrollEnabled(true);
  }, []);

  const keyExtractor = useCallback((item: IUserImageAreaModel) => `${item.productId}-${item.id}`, []);

  const viewabilityConfig = useMemo(
    () => ({
      itemVisiblePercentThreshold: isSlideshow ? 100 : 10, // Higher threshold for slideshow
      minimumViewTime: isSlideshow ? 250 : 100, // Longer time for slideshow
    }),
    [isSlideshow],
  );
  const onViewableItemsChanged = useCallback(
    ({ viewableItems }: any) => {
      // Log degli elementi visibili
      // console.log(
      // 'DEBUG Visible items:',
      // viewableItems.map((item: any) => ({
      // id: item.item.id,
      // index: item.index,
      // isViewable: item.isViewable,
      // })),
      // );

      // Calcolo dell'elemento centrale basato su numColumns
      if (viewableItems.length > 0) {
        let centralElement = null;

        if (isSlideshow) {
          // Per slideshow: elemento con maggior percentuale di visibilità
          centralElement = viewableItems.reduce((max: any, current: any) =>
            (current.visiblePercentage || 0) > (max.visiblePercentage || 0) ? current : max,
          );

          // console.log("DEBUG Visible items -  centralElement.index", centralElement.index, viewableItems);
          // aggiorna activeIndex se slideshow
          // setActiveIndex(centralElement.index);
          // scroll to centralElement
          // scrollToIndex(activeIndex, false);
        } else if (numColumns > 0) {
          // Per griglia: calcolare l'elemento più vicino al centro
          const visibleIndices = viewableItems.map((item: any) => item.index).sort((a: number, b: number) => a - b);
          const minIndex = visibleIndices[0];
          const maxIndex = visibleIndices[visibleIndices.length - 1];

          // Calcolare la riga centrale tra quelle visibili
          const minRow = Math.floor(minIndex / numColumns);
          const maxRow = Math.floor(maxIndex / numColumns);
          const centralRow = Math.floor((minRow + maxRow) / 2);

          // Nella riga centrale, trovare la colonna centrale
          const centralColumn = Math.floor((numColumns - 1) / 2);
          const centralIndex = centralRow * numColumns + centralColumn;

          // Trovare l'elemento visibile più vicino all'indice centrale calcolato
          centralElement = viewableItems.reduce((closest: any, current: any) => {
            const closestDistance = Math.abs(closest.index - centralIndex);
            const currentDistance = Math.abs(current.index - centralIndex);
            return currentDistance < closestDistance ? current : closest;
          });
        } else {
          // Per stacked: primo elemento visibile (ignorato per ora)
          // centralElement = viewableItems[0];
        }

        if (centralElement) {
          // console.log('DEBUG Visible items - Central element:', {
          //   id: centralElement.item.id,
          //   index: centralElement.index,
          //   numColumns: numColumns,
          //   mode: isSlideshow ? 'slideshow' : isGrid ? 'grid' : 'stacked',
          // });

          onCurrentProductChangedDebounced(centralElement.item.id);
        }
      }

      // Aggiorna activeIndex per SliderControls in slideshow
      // if (isSlideshow && viewableItems.length > 0) {
      //   const firstVisibleIndex = viewableItems[0].index;
      //   setActiveIndex(firstVisibleIndex);
      // }
    },
    [isSlideshow, numColumns],
  );

  const handleContainerLayout = (event: LayoutChangeEvent) => {
    'worklet';
    const { width, height } = event.nativeEvent.layout;

    // Update container dimensions
    containerDimensionsSV.value = { width, height };

    let itemWidth;
    let itemHeight;
    let paddingHorizontal = 0;

    if (isSlideshow) {
      // In modalità slideshow, usiamo il 35% della larghezza della container per ogni item
      itemWidth = width * 0.35;
      // itemWidth = width * 0.55;

      paddingHorizontal = (width - itemWidth) / 2;

      // Calcoliamo l'altezza disponibile
      const availableHeight = height - GRID_HEADER_HEIGHT - 60; // 60px margine totale

      // Per slideshow vogliamo che gli elementi siano più grandi
      // Usiamo il minimo tra:
      // 1. L'altezza disponibile
      // 2. La larghezza (per mantenere un aspect ratio massimo di 1:1)
      const maxItemHeight = Math.min(availableHeight, itemWidth);

      // Ma assicuriamoci che sia almeno il 70% della larghezza per non avere elementi troppo piccoli
      itemHeight =
        Math.max(itemWidth * 0.7, Math.min(maxItemHeight, availableHeight)) +
        GRID_ITEM_TITLE_HEIGHT +
        GRID_ITEM_ACTIONS_HEIGHT;
    } else {
      // isGrid
      paddingHorizontal = 16;
      itemWidth = (width - 68 * 2) / numColumns;
      itemHeight = itemWidth + 48 + 24;
    }

    itemSizeSV.value = { width: itemWidth, height: itemHeight, paddingHorizontal };
  };

  const animatedItemWrapperStyle = useAnimatedStyle(() => {
    'worklet';

    return {
      width: itemSizeSV.value.width,
      height: itemSizeSV.value.height,
    };
  });

  const renderItem = useCallback(
    ({ item: userImageArea, index }: { item: IUserImageAreaModel; index: number }) => {
      return (
        <Animated.View key={userImageArea.id} style={animatedItemWrapperStyle}>
          <CropPreview
            userImageArea={userImageArea}
            index={index}
            editable
            showGrid
            itemSizeSV={itemSizeSV}
            onPanStart={handlePanStart}
            onPanEnd={handlePanEnd}
          />
        </Animated.View>
      );
    },
    [animatedItemWrapperStyle, itemSizeSV, handlePanStart, handlePanEnd],
  );

  return (
    <FlatList
      onLayout={handleContainerLayout}
      ref={flatListRef}
      data={userImageAreas}
      key={`FlatList-${numColumns}`}
      keyExtractor={keyExtractor}
      numColumns={numColumns > 0 ? numColumns : undefined} // undefined se slideshow
      horizontal={isSlideshow}
      showsHorizontalScrollIndicator={true}
      pagingEnabled={false}
      snapToAlignment={isSlideshow ? 'center' : undefined}
      decelerationRate={isSlideshow ? 'fast' : 'normal'}
      renderItem={renderItem}
      onViewableItemsChanged={onViewableItemsChanged}
      viewabilityConfig={viewabilityConfig}
      removeClippedSubviews={true}
      maxToRenderPerBatch={isSlideshow ? 3 : 10}
      windowSize={isSlideshow ? 3 : 5}
      initialNumToRender={isSlideshow ? 1 : numColumns * 2}
      updateCellsBatchingPeriod={50}
      contentContainerStyle={[
        (isSlideshow || numColumns === 1) && {
          alignItems: 'center',
        },
        isDesktop && { paddingHorizontal: 68, gap: 48, paddingTop: 100, paddingBottom: 24 },
        !isDesktop && { paddingTop: 68, paddingBottom: 100 },
      ]}
      columnWrapperStyle={isGrid && numColumns !== 1 && { position: 'static' }} // not supported for isSlideshow
      scrollEnabled={scrollEnabled}
    />
  );
};
